// api
import type { CreateUnitDto } from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import { EMPTY_ADDRESS_VALUE } from '@/components/ui/form';

// relative
import type {
  CreateUnitFormValues,
  SelectedUnitType,
  UnitTypeInputFormValue,
} from './createUnitForm.schema';

export function toCreateUnitFormValues(): CreateUnitFormValues {
  return {
    name: '',
    description: '',
    address: EMPTY_ADDRESS_VALUE,
    tags: [],
    unitType: null,
    unitTypeInputs: {},
  };
}

export function buildInitialUnitTypeInputs(
  unitType: SelectedUnitType | null,
): Record<string, UnitTypeInputFormValue> {
  if (!unitType?.UnitTypeInputs) return {};

  const initialInputs: Record<string, UnitTypeInputFormValue> = {};
  unitType.UnitTypeInputs.forEach((input) => {
    initialInputs[input.UnitTypeInputId] = {
      inputType: 'sensorId',
      sensorId: '',
      externalSensorId: '',
      apiInput: false,
      customName: '',
      channelNumber: null,
    };
  });
  return initialInputs;
}

export function toPostUnitPayload(
  values: CreateUnitFormValues,
  accountId: number,
): CreateUnitDto {
  const hasCoords =
    values.address.latitude != null && values.address.longitude != null;

  // The generated `CreateUnitDto`/`WriteUnitSensorDto` types don't perfectly
  // line up with the values this form produces (e.g. `ChannelNumber` is
  // declared as a required `number`, but this form leaves it `null` for
  // non-sensor inputs) - same cast-once pattern as editUnitForm.mappers.ts's
  // `toPatchUnitPayload`.
  return {
    Name: values.name.trim(),
    Description: values.description.trim() || null,
    Latitude: hasCoords ? values.address.latitude : null,
    Longitude: hasCoords ? values.address.longitude : null,
    GpsAccuracy: 0,
    UnitSensors: Object.entries(values.unitTypeInputs)
      .filter(
        ([, inputData]) =>
          (inputData.inputType === 'sensorId' && inputData.sensorId) ||
          (inputData.inputType === 'externalSensorId' &&
            inputData.externalSensorId) ||
          inputData.inputType === 'apiInput',
      )
      .map(([inputId, inputData]) => ({
        UnitTypeInput: { UnitTypeInputId: parseInt(inputId, 10) },
        Sensor:
          inputData.inputType === 'sensorId'
            ? { SensorId: parseInt(inputData.sensorId, 10) }
            : null,
        ExternalSensor:
          inputData.inputType === 'externalSensorId'
            ? { ExternalSensorId: parseInt(inputData.externalSensorId, 10) }
            : null,
        ApiInput: inputData.inputType === 'apiInput',
        ChannelNumber: inputData.channelNumber ?? 1,
        CustomName: inputData.customName?.trim() || null,
      })),
    Account: { AccountId: accountId },
    // `values.unitType` is guaranteed non-null here: the zod schema's
    // `.refine()` rejects a null unit type, so this mapper only ever runs
    // after that validation has passed (from the RHF submit handler).
    UnitType: { UnitTypeId: (values.unitType as SelectedUnitType).UnitTypeId },
  } as CreateUnitDto;
}
